import React, { useEffect, useState } from 'react';
import { API_URL } from '../api/config';
import { apiGet, apiRequest } from '../utils/api';

type BlogPost = {
  id: number;
  slug: string;
  title: string;
  excerpt?: string;
  body: string;
  author?: string;
  category?: string;
  tags?: string[];
  image_url?: string;
  published: boolean;
  featured: boolean;
  published_at?: string;
  created_at: string;
  updated_at?: string;
};

type StoryDraft = {
  id?: number;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  author: string;
  category: string;
  tags: string;
  image_url: string;
  published: boolean;
  featured: boolean;
};

const emptyStoryDraft: StoryDraft = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  author: 'eSanza Team',
  category: 'Field Story',
  tags: '',
  image_url: '',
  published: false,
  featured: false,
};

const formatDate = (value?: string) => {
  if (!value) return 'Not updated';
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const resolveMediaUrl = (value?: string) => {
  if (!value) return '';
  if (value.startsWith('http') || value.startsWith('blob:')) return value;
  return `${API_URL}${value}`;
};

const BlogPage: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [storyDraft, setStoryDraft] = useState<StoryDraft>(emptyStoryDraft);
  const [storyImage, setStoryImage] = useState<File | null>(null);
  const [storyPreview, setStoryPreview] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'saving' | 'saved' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const loadPosts = async () => {
    setStatus('loading');
    setMessage('');
    try {
      const blogData = await apiGet<{ success: boolean; posts: BlogPost[] }>('/api/website-control/blog-posts');
      setBlogPosts(blogData.posts || []);
      setStatus('idle');
    } catch (error: any) {
      setMessage(error.message || 'Could not load blog posts.');
      setStatus('error');
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const editStory = (post?: BlogPost) => {
    if (!post) {
      setStoryDraft(emptyStoryDraft);
      setStoryImage(null);
      setStoryPreview('');
      return;
    }
    setStoryDraft({
      id: post.id,
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      body: post.body || '',
      author: post.author || 'eSanza Team',
      category: post.category || 'Field Story',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
      image_url: post.image_url || '',
      published: post.published === true,
      featured: post.featured === true,
    });
    setStoryImage(null);
    setStoryPreview(resolveMediaUrl(post.image_url));
  };

  const saveStory = async () => {
    setStatus('saving');
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('title', storyDraft.title);
      formData.append('slug', storyDraft.slug);
      formData.append('excerpt', storyDraft.excerpt);
      formData.append('body', storyDraft.body);
      formData.append('author', storyDraft.author);
      formData.append('category', storyDraft.category);
      formData.append('published', String(storyDraft.published));
      formData.append('featured', String(storyDraft.featured));
      formData.append('image_url', storyDraft.image_url);
      storyDraft.tags.split(',').map((tag) => tag.trim()).filter(Boolean).forEach((tag) => {
        formData.append('tags', tag);
      });
      if (storyImage) formData.append('image', storyImage);

      const endpoint = storyDraft.id
        ? `/api/website-control/blog-posts/${storyDraft.id}?type=blog`
        : '/api/website-control/blog-posts?type=blog';
      const data = await apiRequest<{ success: boolean; post: BlogPost }>(endpoint, {
        method: storyDraft.id ? 'PATCH' : 'POST',
        body: formData,
      });

      setBlogPosts((prev) => {
        const exists = prev.some((post) => post.id === data.post.id);
        return exists
          ? prev.map((post) => post.id === data.post.id ? data.post : post)
          : [data.post, ...prev];
      });
      editStory(data.post);
      setStatus('saved');
      setMessage('Story saved successfully.');
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Could not save story.');
    }
  };

  const deletePost = async (post: BlogPost) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    setStatus('saving');
    setMessage('');
    try {
      await apiRequest(`/api/website-control/blog-posts/${post.id}`, { method: 'DELETE' });
      setBlogPosts((prev) => prev.filter((p) => p.id !== post.id));
      if (storyDraft.id === post.id) editStory();
      setStatus('saved');
      setMessage(`"${post.title}" deleted.`);
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Could not delete post.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-700">Content management</p>
          <h1 className="mt-2 text-3xl font-black text-slate-950">Blog Posts</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Create, edit, and publish blog stories. Published posts appear on the public website at /blog.
          </p>
        </div>
        <button
          onClick={loadPosts}
          disabled={status === 'loading'}
          className="rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-extrabold text-slate-700 hover:border-emerald-700 hover:text-emerald-700 disabled:opacity-60"
        >
          Refresh
        </button>
      </div>

      {message && (
        <div className={`rounded-lg border p-4 text-sm font-semibold ${status === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {message}
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[420px_1fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-black text-slate-950">{storyDraft.id ? 'Edit story' : 'Create story'}</h2>
              <p className="mt-1 text-sm text-slate-500">Upload a story image and publish it to the public website.</p>
            </div>
            <button
              type="button"
              onClick={() => editStory()}
              className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50"
            >
              New
            </button>
          </div>

          <div className="mt-5 space-y-4">
            <label className="block">
              <span className="text-sm font-bold text-slate-700">Story image</span>
              <div className="mt-2 overflow-hidden rounded-xl border border-dashed border-slate-300 bg-slate-50">
                {storyPreview ? (
                  <img src={storyPreview} alt="" className="h-52 w-full object-cover" />
                ) : (
                  <div className="flex h-52 items-center justify-center text-sm font-semibold text-slate-400">
                    No image selected
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setStoryImage(file);
                  setStoryPreview(file ? URL.createObjectURL(file) : resolveMediaUrl(storyDraft.image_url));
                }}
                className="mt-3 block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-emerald-50 file:px-4 file:py-2 file:text-sm file:font-bold file:text-emerald-700 hover:file:bg-emerald-100"
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-slate-700">Title</span>
              <input
                value={storyDraft.title}
                onChange={(event) => setStoryDraft((prev) => ({ ...prev, title: event.target.value }))}
                className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
              />
            </label>
            <label className="block">
              <span className="text-sm font-bold text-slate-700">Slug</span>
              <input
                value={storyDraft.slug}
                onChange={(event) => setStoryDraft((prev) => ({ ...prev, slug: event.target.value }))}
                placeholder="Auto-generated from title if empty"
                className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
              />
            </label>
            <label className="block">
              <span className="text-sm font-bold text-slate-700">Excerpt</span>
              <textarea
                value={storyDraft.excerpt}
                onChange={(event) => setStoryDraft((prev) => ({ ...prev, excerpt: event.target.value }))}
                rows={3}
                className="mt-2 w-full resize-none rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
              />
            </label>
            <label className="block">
              <span className="text-sm font-bold text-slate-700">Story body</span>
              <textarea
                value={storyDraft.body}
                onChange={(event) => setStoryDraft((prev) => ({ ...prev, body: event.target.value }))}
                rows={8}
                className="mt-2 w-full resize-y rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
              />
            </label>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Author</span>
                <input
                  value={storyDraft.author}
                  onChange={(event) => setStoryDraft((prev) => ({ ...prev, author: event.target.value }))}
                  className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Category</span>
                <input
                  value={storyDraft.category}
                  onChange={(event) => setStoryDraft((prev) => ({ ...prev, category: event.target.value }))}
                  className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-sm font-bold text-slate-700">Tags</span>
              <input
                value={storyDraft.tags}
                onChange={(event) => setStoryDraft((prev) => ({ ...prev, tags: event.target.value }))}
                placeholder="traceability, training, avocado"
                className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-700"
              />
            </label>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-4">
                <span>
                  <span className="block text-sm font-bold text-slate-900">Published</span>
                  <span className="mt-1 block text-xs text-slate-500">Show on public blog page.</span>
                </span>
                <input type="checkbox" checked={storyDraft.published} onChange={(event) => setStoryDraft((prev) => ({ ...prev, published: event.target.checked }))} className="mt-1 h-5 w-5" />
              </label>
              <label className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-4">
                <span>
                  <span className="block text-sm font-bold text-slate-900">Featured</span>
                  <span className="mt-1 block text-xs text-slate-500">Pin above normal stories.</span>
                </span>
                <input type="checkbox" checked={storyDraft.featured} onChange={(event) => setStoryDraft((prev) => ({ ...prev, featured: event.target.checked }))} className="mt-1 h-5 w-5" />
              </label>
            </div>
            <button
              type="button"
              onClick={saveStory}
              disabled={status === 'saving' || !storyDraft.title.trim() || !storyDraft.body.trim()}
              className="w-full rounded-lg bg-emerald-700 px-5 py-3 text-sm font-extrabold text-white hover:bg-emerald-800 disabled:opacity-60"
            >
              {status === 'saving' ? 'Saving...' : 'Save story'}
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-5">
            <h2 className="text-lg font-black text-slate-950">Story library</h2>
            <p className="mt-1 text-sm text-slate-500">{blogPosts.length} stories managed from dashboard.</p>
          </div>
          <div className="divide-y divide-slate-100">
            {blogPosts.length === 0 ? (
              <div className="p-10 text-center text-slate-500">No stories yet.</div>
            ) : blogPosts.map((post) => (
              <div
                key={post.id}
                className="group flex gap-4 p-5 transition hover:bg-slate-50"
              >
                <button
                  type="button"
                  onClick={() => editStory(post)}
                  className="flex flex-1 gap-4 text-left"
                >
                  <div className="h-24 w-32 flex-shrink-0 overflow-hidden rounded-xl bg-slate-100">
                    {post.image_url ? (
                      <img src={resolveMediaUrl(post.image_url)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs font-bold text-slate-400">No image</div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-black text-slate-950">{post.title}</h3>
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${post.published ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-500'}`}>
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                      {post.featured && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">Featured</span>}
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500">{post.excerpt || post.body}</p>
                    <p className="mt-2 text-xs font-semibold text-slate-400">{formatDate(post.updated_at || post.created_at)}</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => deletePost(post)}
                  className="self-center rounded-lg p-2 text-slate-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                  title="Delete post"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
